// @flow strict
import * as React from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Partner, getPartnerByCode } from './partner.js'
import { getStateAldData } from './state-ald-data'
import log from 'loglevel'
import type { Grade, Subject } from './ald-types'

const LOG_LEVEL = 'info'//https://github.com/pimterry/loglevel
log.setLevel(LOG_LEVEL)
log.debug(`Log level set to ${LOG_LEVEL}`)

const paramNames = {
  partner: 'partner',
  grade: 'grade',
  subject: 'subject',
  achievementLevel: 'level'
}
let aldData: Grade[]

const getPartnerFromQuery = (params: URLSearchParams): ?Partner => {
  const queryPartner = params.get(paramNames.partner)
  if (queryPartner) {
    return getPartnerByCode(queryPartner)
  }
  return null
}

const getGradeFromQuery = (params: URLSearchParams): ?string => {
  const queryGrade = params.get(paramNames.grade)
  return queryGrade ? `Grade ${queryGrade}` : null
}

const getSubjectFromQuery = (params: URLSearchParams, subjects: Array<Subject>): ?string => {
  let querySubject = params.get(paramNames.subject)
  if (querySubject) {
    querySubject = querySubject.toLowerCase()
    log.debug(`Using subject param value of ${querySubject}`)
    if (subjects) {

      const matchingSubject = subjects.filter((subject) => {
        return subject.subjectId.toLowerCase() === querySubject
      })

      if (!matchingSubject || matchingSubject.length !== 1) log.error(`A matching subject was not found in partner data`)
      log.trace(`Display of subject is ${JSON.stringify(matchingSubject[0])}`)
      log.debug(`Using partner subject ${matchingSubject[0].subjectDisplay}`)
      return matchingSubject[0].subjectDisplay
    }
    //Do we even need this?
    log.warn(`Using fallback`)
    switch (querySubject) {
      case 'math':
        return 'Math'
      case 'ela':
        return 'ELA'
      default:
        return null
    }
  }
  return null
}

const getLevelFromParams = async (params: URLSearchParams, subjects: Array<Subject>): Promise<?string> => {
  //first handle the partner param to load the aldData
  const partnerParamVal = params.get(paramNames.partner)
  const achievementLevelName = params.get(paramNames.achievementLevel)

  log.debug(`partner:${partnerParamVal || 'null'} aLevel:${achievementLevelName || 'null'}`)

  if (!partnerParamVal || !params.get(paramNames.grade) || !params.get(paramNames.subject)) {
    log.info(`Don't have enough param data to preselect data using url params`)
    return null
  }

  //now the subject
  const subjectId = getSubjectFromQuery(params, subjects)
  if (!subjectId) {
    log.error('No subject can be determined from the parameters')
    return null
  }
  const subjectData = subjects.filter(aldSubject => {
    log.debug(`Comparing subjectId ${subjectId} to ${aldSubject.subjectId}`)
    return aldSubject.subjectId === subjectId
  })[0]
  if (!subjectData) log.error(`No matching subject is found for subjectId:${subjectId}`)
  log.trace('Subject Data:' + JSON.stringify(subjectData))
  const matchingLevel = subjectData.achievementLevels.filter(aldLevel => aldLevel.achievementLevelConfig.paramName === achievementLevelName)
  if (matchingLevel.length === 0) {
    log.warn('No matching level data')
    return Promise.resolve()
  }

  return Promise.resolve(matchingLevel[0].achievementLevelConfig.name)
}

const UrlParamHandler = (): React.Node => {
  const location = useLocation()
  const navigate = useNavigate()

  if (location.search) {
    const params = new URLSearchParams(location.search)
    const partnerParamVal = params.get(paramNames.partner)
    const partner: Partner = getPartnerByCode(partnerParamVal || undefined) || Partner.Default
    const gradeId = getGradeFromQuery(params)

    const checkData = new Promise((resolve, reject) => {
      if (aldData) return resolve(aldData);
      return getStateAldData(partner).then(data=>{resolve(data)});
    })

    let gradeData: Grade;
    checkData.then((stateData) => {
      aldData = stateData;
      log.trace(`aldData:${JSON.stringify(aldData)}`);
      gradeData = aldData.filter(aldGrade => aldGrade.gradeId === gradeId)[0];
      if (!gradeData) log.error(`Unable to locate matching grade data for gradeId:${gradeId || 'undefined'}`)
    }).then(() => getLevelFromParams(params, gradeData.availableSubjects)).then(level => {
      const displaySkills = {
        selectedPartnerId: getPartnerFromQuery(params),
        selectedGradeId: getGradeFromQuery(params),
        selectedSubjectId: getSubjectFromQuery(params, gradeData.availableSubjects),
        selectedAchievementLevel: level
      }
      log.debug(`Replacing history with ${JSON.stringify(displaySkills)}`)
      navigate('/display-skills', { state: displaySkills })
    }).catch(e => {
      console.error(`Error while processing query parameters: ${e.message || e}, user will be returned to the application entry page.`)
    })
  }
  return null
}

export default UrlParamHandler
